/**
 * Analytics / dataLayer event helpers
 *
 * All events follow the GA4 recommended event structure and are pushed to
 * window.dataLayer for GTM to pick up.
 *
 * Every event includes te_visitor_id + te_funnel_stage for cross-session
 * user identification and remarketing audience segmentation.
 *
 * Funnel stages (one-way progression):
 *   visitor → lead → registered → applicant → package_selected → submitted
 *
 * Events:
 *   te_user_init         → on app load (sets user context in GA4 + Ads)
 *   te_user_login        → authenticated user session identified
 *   generate_lead        → contact form submitted
 *   register_user        → account created (OTP magic link sent)
 *   applicant_started    → wizard first opened
 *   membership_selected  → user picked a package (GA4 view_item)
 *   application_submitted → full application submitted (GA4 purchase + Ads conversion)
 *
 * User properties (sent on every te_user_init / te_user_login):
 *   te_funnel_stage, te_visitor_id, te_language
 *
 * Currency: THB for display, USD for Google Ads ROAS (÷ 36 exchange rate)
 */

import {
  getOrCreateVisitorId,
  getFunnelStage,
  advanceFunnelStage,
  type FunnelStage,
} from "@/lib/visitor";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    gtag: (...args: unknown[]) => void;
  }
}

// ─── Package data ─────────────────────────────────────────────────────────────

/** Commission-adjusted USD value for Google Ads ROAS (fee × 5% commission ÷ 36 THB/USD).
 *  Keep in step with app_settings.conversion_commission_rate (server-side Meta / Google Ads member values). */
const PACKAGE_FEE_THB: Record<string, number> = {
  bronze:    650_000,
  gold:      900_000,
  platinum: 1_500_000,
  diamond:  2_500_000,
  reserve:  5_000_000,
  // short codes (legacy)
  bz: 650_000,
  gd: 900_000,
  pt: 1_500_000,
  dm: 2_500_000,
  rs: 5_000_000,
};

const PACKAGE_LABEL: Record<string, string> = {
  bronze: "Bronze", bz: "Bronze",
  gold: "Gold",     gd: "Gold",
  platinum: "Platinum", pt: "Platinum",
  diamond: "Diamond",   dm: "Diamond",
  reserve: "Reserve",   rs: "Reserve",
};

const THB_TO_USD = 36; // approximate, update quarterly
const COMMISSION_RATE = 0.05; // = app_settings.conversion_commission_rate

function feeUSD(packageCode: string): number {
  const code = packageCode.toLowerCase();
  const thb  = PACKAGE_FEE_THB[code] ?? 0;
  return Math.round((thb * COMMISSION_RATE) / THB_TO_USD); // commission in USD
}
function feeTHB(packageCode: string): number {
  return PACKAGE_FEE_THB[packageCode.toLowerCase()] ?? 0;
}

/**
 * E.164 phone for enhanced conversions / Meta advanced matching
 * ("+44 7700-900123" → "+447700900123"). Returns undefined when the number has
 * no country code, since the platforms can't match it.
 */
export function toE164(phone?: string | null): string | undefined {
  if (!phone) return undefined;
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (trimmed.startsWith("+")) return digits.length >= 8 ? `+${digits}` : undefined;
  if (trimmed.startsWith("00")) return digits.length >= 10 ? `+${digits.slice(2)}` : undefined;
  return undefined;
}

/**
 * Persona + language from a landing-page ref code ("lp-en-retire", carried by
 * the "Apply online" links of lp.thailand-elite.com / thailandeelite.com).
 */
export function personaFromRef(ref?: string | null): { persona?: string; lp_language?: string } {
  const m = /^lp-([a-z]{2})(?:-([a-z-]+))?$/.exec(ref ?? "");
  return m ? { lp_language: m[1], persona: m[2] || "main" } : {};
}

/** Tracking-spec user data: plain values in the dataLayer, hashed by GTM (never put in URLs). */
function userData(email?: string | null, phone?: string | null) {
  const e = email?.trim().toLowerCase();
  return { email: e || undefined, phone: toE164(phone) };
}

// ─── Core push helper ─────────────────────────────────────────────────────────

function push(
  event: string,
  newStage: FunnelStage | null,
  params: Record<string, unknown> = {}
) {
  window.dataLayer = window.dataLayer || [];
  if (newStage) advanceFunnelStage(newStage);
  window.dataLayer.push({
    event,
    te_visitor_id:   getOrCreateVisitorId(),
    te_funnel_stage: getFunnelStage(),
    ...params,
  });
}

/** Push user properties to GA4 (identified user context). */
function pushUserProperties(userId?: string) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "te_set_user_properties",
    te_visitor_id:   getOrCreateVisitorId(),
    te_funnel_stage: getFunnelStage(),
    te_user_id:      userId ?? null,
    te_language:     navigator.language?.slice(0, 2) || "en",
  });
}

// ─── Public helpers ────────────────────────────────────────────────────────────

/**
 * Call once on app mount — pushes visitor ID + current funnel stage to GTM.
 * Also sends user_properties to GA4 (used by Audiences and remarketing).
 */
export function initUserContext(userId?: string) {
  pushUserProperties(userId);
  push("te_user_init", null, {
    te_user_id:  userId ?? null,
    te_language: navigator.language?.slice(0, 2) || "en",
  });
}

/**
 * Call when an authenticated session is identified (login / session restore).
 * GA4 uses this to stitch cross-session user journeys.
 */
export function trackLogin(userId: string, method: "magic_link" | "password" = "password") {
  push("te_user_login", null, {
    te_user_id: userId,
    method,
    // GA4 recommended login event
    event_category: "auth",
  });
  // Also push user_id as GA4 user property
  window.dataLayer.push({
    event:      "te_set_user_properties",
    te_user_id: userId,
    te_funnel_stage: getFunnelStage(),
  });
}

/**
 * Contact form successfully submitted.
 * Advances stage: visitor → lead
 */
export function trackGenerateLead(params?: {
  subject?: string;
  language?: string;
  /** Same ID stored on the lead row — lets Meta/Google dedupe browser vs server events. */
  eventId?: string;
  /** Landing page / form name, e.g. "lp-de". Defaults to the contact form. */
  method?: string;
  /** leads.id returned by contact-notify. */
  leadId?: string | null;
  email?: string | null;
  phone?: string | null;
}) {
  push("generate_lead", "lead", {
    event_category: "contact",
    event_label:    params?.subject || "contact_form",
    language:       params?.language,
    // GA4 recommended
    method: params?.method || "contact_form",
    event_id: params?.eventId,
    transaction_id: params?.eventId,
    // Tracking spec (Google Ads lead tag + Meta Lead): placeholder value until CRM values exist.
    lead_id: params?.leadId ?? undefined,
    value: 1000,
    currency: "THB",
    persona: "main",
    lp_language: params?.language,
    ...userData(params?.email, params?.phone),
  });
}

/**
 * User created account (OTP magic link sent).
 * Advances stage: lead → registered
 */
export function trackRegisterUser(params?: { userId?: string; language?: string }) {
  push("register_user", "registered", {
    event_category: "registration",
    event_label:    "magic_link",
    method:         "magic_link",
    te_user_id:     params?.userId ?? null,
    language:       params?.language,
  });
}

/**
 * User entered the portal application wizard.
 * Advances stage: registered → applicant
 */
export function trackApplicantStarted(applicationId?: string) {
  // The wizard re-mounts on every step, reload and return visit. Count one
  // start per application, otherwise a single applicant inflates the
  // "applicant_started" conversion many times over.
  if (applicationId) {
    const key = `te_as_${applicationId}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch { /* storage blocked — fire once for this page view */ }
  }
  push("applicant_started", "applicant", {
    application_id: applicationId,
    event_category: "application",
    event_label:    "wizard_start",
  });
}

/**
 * User selected a membership package.
 * Advances stage: applicant → package_selected
 * Also fires GA4 view_item for e-commerce reporting.
 */
export function trackMembershipSelected(packageCode: string) {
  const code  = packageCode.toLowerCase();
  const label = PACKAGE_LABEL[code] ?? packageCode;
  const thb   = feeTHB(code);
  const usd   = feeUSD(code);

  push("membership_selected", "package_selected", {
    event_category:     "application",
    membership_package: label,
    membership_code:    code,
    value:              usd,
    value_thb:          thb,
    currency:           "USD",
    // GA4 e-commerce view_item
    items: [{
      item_id:       `te_${code}`,
      item_name:     `Thailand Elite — ${label}`,
      item_category: "membership",
      price:         usd,
      quantity:      1,
    }],
  });
}

/**
 * Full application submitted (ReviewStep).
 * Advances stage: package_selected → submitted
 *
 * Fires:
 *  - GA4 purchase event (e-commerce revenue reporting)
 *  - Google Ads conversion (via GTM trigger on application_submitted)
 *  - value in USD for ROAS calculation
 */
export function trackApplicationSubmitted(
  packageCode: string,
  transactionId?: string,
  contact?: { email?: string | null; phone?: string | null; refCode?: string | null },
) {
  // Pass the application ID as transactionId: Google Ads dedupes on order_id
  // and Meta on event_id, so a retried submit or an offline upload of the
  // same application never counts twice.
  const code  = packageCode.toLowerCase();
  const label = PACKAGE_LABEL[code] ?? packageCode;
  const thb   = feeTHB(code);
  const usd   = feeUSD(code);
  const txId  = transactionId ?? `te_${Date.now()}_${getOrCreateVisitorId().slice(-8)}`;

  push("application_submitted", "submitted", {
    event_category:     "application",
    event_label:        "review_submit",
    membership_package: label,
    membership_code:    code,
    // GA4 e-commerce purchase fields
    transaction_id: txId,
    event_id:       txId,
    // Tracking spec: application_id for joins, user data for enhanced conversions,
    // persona/language from the landing-page ref when the visit started there.
    application_id: transactionId,
    ...userData(contact?.email, contact?.phone),
    ...personaFromRef(contact?.refCode),
    value:          usd,
    value_thb:      thb,
    currency:       "USD",
    items: [{
      item_id:       `te_${code}`,
      item_name:     `Thailand Elite — ${label}`,
      item_category: "membership",
      price:         usd,
      quantity:      1,
    }],
  });
}
